import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Edit,
  Check,
  X,
  Ban,
  Loader2,
  Download
} from 'lucide-react';
import Layout from '../components/Layout';
import materialRequisitionService from '../services/materialRequisitionService';
import authService from '../services/authService';
import FileAttachmentManager from '../components/FileAttachmentManager';

const MaterialRequisitionDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const user = authService.getCurrentUser();
  const canApprove = user && ['admin', 'warehouse_manager'].includes(user.role);
  
  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionDialog, setActionDialog] = useState({ 
    open: false, 
    action: '', 
    reason: '' 
  });

  const loadRequisition = useCallback(async (force = false) => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await materialRequisitionService.getRequisition(id);
      setRequisition(response.requisition || response);
    } catch (err) {
      setError(err.response?.data?.error || '加载领料申请详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRequisition();
  }, [loadRequisition]);

  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/material-requisitions/${id}/edit`);
    } else if (action === 'export') {
      handleExport();
    } else if (['approve', 'reject', 'cancel'].includes(action)) {
      setActionDialog({ open: true, action, reason: '' });
    }
  };

  const handleExport = async () => {
    try {
      const requisitionNumber = requisition?.requisition_number || requisition?._id || id;
      
      const response = await materialRequisitionService.exportRequisitions({
        requisition_id: requisition._id || id
      });
      
      // Create blob URL and download
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `领料单_${requisitionNumber}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || '导出失败');
    }
  };

  const handleConfirmAction = async () => {
    const { action, reason } = actionDialog;
    setActionDialog({ open: false, action: '', reason: '' });

    try {
      if (action === 'approve') {
        await materialRequisitionService.approveRequisition(id);
        setRequisition(prev => ({ ...prev, status: 'approved' }));
        setError('');
        alert('领料申请已批准！');
      } else if (action === 'reject') {
        await materialRequisitionService.rejectRequisition(id, reason);
        setRequisition(prev => ({ ...prev, status: 'rejected', reject_reason: reason }));
        setError('');
        alert('领料申请已拒绝！');
      } else if (action === 'cancel') {
        await materialRequisitionService.cancelRequisition(id);
        setRequisition(prev => ({ ...prev, status: 'cancelled' }));
        setError('');
        alert('领料申请已取消！');
      }
    } catch (err) {
      setError(err.response?.data?.error || `操作失败: ${action}`);
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-success/10 text-success border-success/30';
      case 'pending_approval':
        return 'bg-warning/10 text-warning border-warning/30';
      case 'rejected':
        return 'bg-destructive/10 text-destructive border-destructive/30';
      case 'cancelled':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending_approval':
        return '待审批';
      case 'approved':
        return '已通过';
      case 'rejected':
        return '已拒绝';
      case 'cancelled':
        return '已取消';
      default:
        return status || '未知';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !requisition) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 px-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => navigate('/material-requisitions')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回列表
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="领料申请详情" activeTab="material-requisitions">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/material-requisitions')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              {requisition?.requisition_number}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {canApprove && requisition?.status === 'pending_approval' && (
              <>
                <Button
                  variant="default"
                  className="bg-success hover:bg-success/90"
                  onClick={() => handleAction('approve')}
                >
                  <Check className="h-4 w-4 mr-2" />
                  批准
                </Button>
                <Button
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive/10"
                  onClick={() => handleAction('reject')}
                >
                  <X className="h-4 w-4 mr-2" />
                  拒绝
                </Button>
              </>
            )}
            {requisition?.status === 'pending_approval' && (
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => handleAction('cancel')}
              >
                <Ban className="h-4 w-4 mr-2" />
                取消申请
              </Button>
            )}
            {requisition?.status !== 'cancelled' && requisition?.status !== 'approved' && (
              <Button variant="outline" onClick={() => handleAction('edit')}>
                <Edit className="h-4 w-4 mr-2" />
                编辑
              </Button>
            )}
            <Button variant="secondary" onClick={() => handleAction('export')}>
              <Download className="h-4 w-4 mr-2" />
              导出领料单
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">申请信息</h2>
                <Separator className="mb-4" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">申请号</p>
                    <p className="font-medium">{requisition?.requisition_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">工单号</p>
                    <p className="font-medium">
                      {requisition?.production_order_id ? (
                        <Link
                          to={`/production/${requisition.production_order_id}`}
                          className="text-primary underline hover:text-primary/80 transition-colors"
                        >
                          {requisition?.work_order_number || '-'}
                        </Link>
                      ) : (
                        requisition?.work_order_number || '-'
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">状态</p>
                    <Badge className={cn('border', getStatusClass(requisition?.status))}>
                      {getStatusLabel(requisition?.status)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">申请人</p>
                    <p className="font-medium">{requisition?.applicant_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">创建时间</p>
                    <p>{formatDate(requisition?.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">更新时间</p>
                    <p>{formatDate(requisition?.updated_at)}</p>
                  </div>
                  {requisition?.department && (
                    <div>
                      <p className="text-sm text-muted-foreground">部门</p>
                      <p className="font-medium">{requisition.department}</p>
                    </div>
                  )}
                  {requisition?.purpose && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">用途说明</p>
                      <p>{requisition.purpose}</p>
                    </div>
                  )}
                  {(requisition?.remarks || requisition?.notes) && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">备注</p>
                      <p>{requisition.remarks || requisition.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">物料明细</h2>
                <Separator className="mb-4" />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-4 py-3 text-left font-medium">产品编码</th>
                        <th className="px-4 py-3 text-left font-medium">产品名称</th>
                        <th className="px-4 py-3 text-left font-medium">规格</th>
                        <th className="px-4 py-3 text-left font-medium">库位</th>
                        <th className="px-4 py-3 text-right font-medium">申请数量</th>
                        <th className="px-4 py-3 text-right font-medium">批准数量</th>
                        <th className="px-4 py-3 text-left font-medium">备注</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {requisition?.products?.map((product, index) => (
                        <tr key={index} className={cn('hover:bg-muted/30', product.is_leftover && 'bg-yellow-50/50')}>
                          <td className="px-4 py-3">
                            {product.product_id || '-'}
                            {product.is_leftover && (
                              <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-600 text-xs">
                                余料
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {product.product_name || '-'}
                            {product.leftover_code && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                ({product.leftover_code})
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">{product.specification || '-'}</td>
                          <td className="px-4 py-3">
                            {product.location_allocations && product.location_allocations.length > 0 ? (
                              <div className="space-y-1">
                                {product.location_allocations.map((alloc, idx) => (
                                  <div key={idx} className="text-xs">
                                    <span className="font-medium">{alloc.location}:</span>
                                    <span className="ml-1">{alloc.quantity}{product.unit || '支'}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              product.location || '-'
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">{product.requested_quantity ?? product.quantity ?? '-'}</td>
                          <td className="px-4 py-3 text-right">{product.approved_quantity ?? '-'}</td>
                          <td className="px-4 py-3">{product.notes || product.remarks || '-'}</td>
                        </tr>
                      ))}
                      {(!requisition?.products || requisition.products.length === 0) && (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                            暂无物料明细
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* File Attachments */}
            <FileAttachmentManager
              entityType="material_requisitions"
              entityId={requisition?._id}
              title="附件列表"
              className="mb-6"
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Approval Information */}
            {(requisition?.status === 'approved' || requisition?.status === 'rejected') && (
              <Card className="border bg-card">
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold mb-2">审批信息</h2>
                  <Separator className="mb-4" />
                  <div className="space-y-3">
                    {requisition?.approver_name && (
                      <div>
                        <p className="text-sm text-muted-foreground">审批人</p>
                        <p className="font-medium">{requisition.approver_name}</p>
                      </div>
                    )}
                    {requisition?.approved_at && (
                      <div>
                        <p className="text-sm text-muted-foreground">审批时间</p>
                        <p>{formatDate(requisition.approved_at)}</p>
                      </div>
                    )}
                    {requisition?.reject_reason && (
                      <div>
                        <p className="text-sm text-muted-foreground">拒绝原因</p>
                        <p className="text-destructive">{requisition.reject_reason}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">申请摘要</h2>
                <Separator className="mb-4" />
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">物料总数</span>
                    <span className="text-sm">{requisition?.products?.length || 0} 项</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">申请数量合计</span>
                    <span className="text-sm">
                      {requisition?.products?.reduce((sum, p) => sum + (p.requested_quantity ?? p.quantity ?? 0), 0) || 0}
                    </span>
                  </div>
                  {requisition?.status === 'approved' && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">批准数量合计</span>
                      <span className="text-sm font-medium text-success">
                        {requisition?.products?.reduce((sum, p) => sum + (p.approved_quantity || 0), 0) || 0}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Dialog */}
        <Dialog
          open={actionDialog.open}
          onOpenChange={(open) => !open && setActionDialog({ open: false, action: '', reason: '' })}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {actionDialog.action === 'approve' && '批准领料申请'}
                {actionDialog.action === 'reject' && '拒绝领料申请'}
                {actionDialog.action === 'cancel' && '取消领料申请'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                {actionDialog.action === 'approve' &&
                  `确定要批准领料申请 ${requisition?.requisition_number} 吗？`
                }
                {actionDialog.action === 'reject' &&
                  `确定要拒绝领料申请 ${requisition?.requisition_number} 吗？`
                }
                {actionDialog.action === 'cancel' &&
                  `确定要取消领料申请 ${requisition?.requisition_number} 吗？此操作不可撤销。`
                }
              </p>
              {actionDialog.action === 'reject' && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="reject-reason">拒绝原因</Label>
                  <Textarea
                    id="reject-reason"
                    rows={4}
                    value={actionDialog.reason}
                    onChange={(e) => setActionDialog(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="请输入拒绝原因..."
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: false, action: '', reason: '' })}
              >
                取消
              </Button>
              <Button
                onClick={handleConfirmAction}
                variant={actionDialog.action === 'reject' || actionDialog.action === 'cancel' ? 'destructive' : 'default'}
                disabled={actionDialog.action === 'reject' && !actionDialog.reason.trim()}
              >
                {actionDialog.action === 'approve' && '批准'}
                {actionDialog.action === 'reject' && '拒绝'}
                {actionDialog.action === 'cancel' && '取消'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MaterialRequisitionDetailPage;